import TitleBar from '../../../components/TitleBar';
import { useState } from 'react';
import QuickEdit from '../../../components/assets/QuickEdit';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../../../components/common/Footer';
import { AntModal, Button, Spinner } from '../../../components';
import { useMutation, useQuery } from '@apollo/client';
import { EDIT_CORPORATE, FETCH_CORPORATE } from '../queries';
import FinancialData from '../../../components/common/FinancialData';
import QueryErrorBoundary from '../../../components/error-handling';
import { ModifyVariablesArgs } from '../../../components/generic/Table/TableV3';
import { useUserContext } from '../../../context/PermissionsContext';
import TitleUpdater from '../../../components/common/TitleUpdater';
import { StandaloneForm } from '../../../components/generic/Form/';

type FindAllCorporates = {
  costOfSales: number;
  grossMargin: number;
  name: string;
  revenue: number;
  uuid: string;
};

type DataReturned = {
  findAllCorporates: FindAllCorporates[];
};

function FinancialsCorporate() {
  const { isAdmin, canEdit, canViewFinancials, isCorporate } = useUserContext();

  const [modal2Open, setModal2Open] = useState(false);
  const { data, loading, error } = useQuery<DataReturned>(FETCH_CORPORATE);
  const navigate = useNavigate();

  const handleCancel = () => {
    setModal2Open(false);
  };

  const [updateCorporate] = useMutation(EDIT_CORPORATE, {
    refetchQueries: ['FetchCorporate'],
  });

  const modifyVariables = ({ values, currentRecord }: ModifyVariablesArgs) => {
    const { revenue, costOfSales } = values || {};
    if (revenue < costOfSales) {
      throw new Error('Revenue cannot be less than Cost of Sales');
    }
    return {
      ...values,
      name: currentRecord?.name,
    };
  };

  const fields = [
    {
      key: 'revenue',
      label: 'Revenue',
      type: 'money',
      required: true,
    },
    {
      key: 'costOfSales',
      label: 'Cost of Sales',
      type: 'money',
      required: true,
    },
  ];

  return (
    <>
      <TitleUpdater title="Corporate Financials" />
      <TitleBar
        title="Data Inputs"
        subtitle="Financials"
        brief="MEO uses Revenue and Gross Margin information to quantify financial exposures."
      />
      <QueryErrorBoundary error={error}>
        {!loading ? (
          <div className="p-10 mb-20">
            <h2 className="  text-primary-200 font-normal text-2xl tracking-wider">
              Corporate Financials
            </h2>
            <hr className="border border-tertiary-100" />
            <div className="my-4 px-12 flex flex-row flex-wrap justify-between items-start w-full">
              <div className="flex gap-6">
                <div>
                  <div className="w-110 my-3 flex items-center justify-between">
                    <label className="font-semibold text-primary-200 text-sm">
                      Revenue
                    </label>
                    <span className="w-64 border-2 rounded-lg text-right text-primary-100 font-semibold text-sm px-2 py-1 hover:border-2 hover:border-blue">
                      {
                        <FinancialData
                          text={data?.findAllCorporates[0]?.revenue || 0}
                          forceHidden={isCorporate}
                        />
                      }
                    </span>
                  </div>
                  <div className="w-110 my-3 flex items-center justify-between">
                    <label className="font-semibold text-primary-200 text-sm">
                      Cost of Sales
                    </label>
                    <span className="w-64 border-2 rounded-lg text-right text-primary-100 font-semibold text-sm px-2 py-1 hover:border-2 hover:border-blue">
                      {
                        <FinancialData
                          text={data?.findAllCorporates[0]?.costOfSales || 0}
                          forceHidden={isCorporate}
                        />
                      }
                    </span>
                  </div>
                  <div className="w-110 my-3 flex items-center justify-between">
                    <label className="font-semibold text-primary-200 text-sm">
                      Gross Margin
                    </label>
                    <span className="w-64 border-2 rounded-lg text-right text-primary-100 font-semibold text-sm px-2 py-1 hover:border-2 hover:border-blue">
                      {
                        <FinancialData
                          text={data?.findAllCorporates[0]?.grossMargin || 0}
                          forceHidden={isCorporate}
                        />
                      }
                    </span>
                  </div>
                </div>
                {isCorporate && (isAdmin || canEdit) && canViewFinancials && (
                  <Button type="edit" onClick={() => setModal2Open(true)}>
                    <QuickEdit />
                  </Button>
                )}
              </div>

              <div className="flex flex-col min-w-[300px]">
                <Button
                  type="primary"
                  className="my-2 py-3"
                  onClick={() =>
                    navigate(
                      `/${data?.findAllCorporates[0]?.name!}/${data
                        ?.findAllCorporates[0]?.uuid!}`,
                    )
                  }
                >
                  Division Financials
                </Button>
                <Button
                  type="primary"
                  className="my-2 py-3"
                  onClick={() =>
                    navigate(
                      `/${
                        data ? data.findAllCorporates[0].uuid : ''
                      }/all-financials`,
                    )
                  }
                >
                  All Financials
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Spinner />
        )}
      </QueryErrorBoundary>
      <Footer backPath={'/data-inputs'} />
      <AntModal
        title="Edit Corporate Financials"
        openModal={modal2Open}
        onCancel={handleCancel}
      >
        <StandaloneForm
          fields={fields}
          updateGQL={updateCorporate}
          modifyVariables={modifyVariables}
          currentRecord={data?.findAllCorporates[0]}
          canDelete={false}
          onFinalize={() => setModal2Open(false)}
        />
      </AntModal>
    </>
  );
}

export default FinancialsCorporate;
